import { useParams, Link, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PageHeader } from "@/components/layout/AppShell";
import { Card } from "@/components/ui/Card";
import { Button, IconButton } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  Tabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  TabsContent,
} from "@/components/ui/Tabs";
import { DetailPageSkeleton } from "@/components/ui/Skeleton";
import {
  ProjectStatusBadge,
  ProcurementMethodBadge,
} from "@/components/shared/StatusBadge";
import { useTender, useTenderLatestVersion } from "@/hooks/useTenders";
import {
  useTenderBookmarkState,
  useToggleTenderBookmark,
} from "@/hooks/useWatchlist";
import { formatDate, formatNumber } from "@/lib/utils";
import { PROVINCE_NAMES } from "@/lib/constants";

// Tab components
import { TenderOverviewTab } from "@/components/tenders/tabs/OverviewTab";
import { TenderTimelineTab } from "@/components/tenders/tabs/TimelineTab";
import { TenderDocumentsTab } from "@/components/tenders/tabs/DocumentsTab";
import { TenderCompaniesTab } from "@/components/tenders/tabs/CompaniesTab";
import { TenderRfqTab } from "@/components/tenders/tabs/RfqTab";

export function TenderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toggleTenderBookmark = useToggleTenderBookmark();
  const tabRaw = searchParams.get("tab");
  const activeTab = (() => {
    switch (tabRaw) {
      case "overview":
      case "timeline":
      case "documents":
      case "companies":
      case "rfq":
        return tabRaw;
      default:
        return "overview";
    }
  })();

  const { data: tender, isLoading: tenderLoading } = useTender(id!);
  const { data: latestVersion, isLoading: versionLoading } =
    useTenderLatestVersion(id!);
  const { data: bookmarkState } = useTenderBookmarkState(id);

  const isLoading = tenderLoading || versionLoading;

  if (isLoading) {
    return <DetailPageSkeleton />;
  }

  if (!tender || !latestVersion) {
    return (
      <Card className="p-8 text-center">
        <Icon
          icon="solar:file-remove-line-duotone"
          className="w-16 h-16 text-slate-300 mx-auto mb-4"
        />
        <h2 className="font-display text-xl font-semibold text-slate-900">
          ไม่พบประกาศ
        </h2>
        <p className="text-slate-500 mt-2">ประกาศนี้อาจถูกลบหรือไม่มีอยู่</p>
        <Link to="/tenders">
          <Button className="mt-4">
            <Icon
              icon="solar:arrow-left-line-duotone"
              className="w-4 h-4 mr-2"
            />
            กลับไปหน้าประกาศ
          </Button>
        </Link>
      </Card>
    );
  }

  const breadcrumbs = [
    { label: "ประกาศจัดซื้อจัดจ้าง", href: "/tenders" },
    { label: latestVersion.project_name?.slice(0, 40) + "..." || "ประกาศ" },
  ];

  const setActiveTab = (nextTab: string) => {
    const params = new URLSearchParams(searchParams);
    if (nextTab === "overview") {
      params.delete("tab");
    } else {
      params.set("tab", nextTab);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title=""
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              icon={
                bookmarkState?.isBookmarked
                  ? "solar:bookmark-bold"
                  : "solar:bookmark-line-duotone"
              }
              label={bookmarkState?.isBookmarked ? "ยกเลิกบันทึก" : "บันทึก"}
              variant={bookmarkState?.isBookmarked ? "secondary" : "ghost"}
              loading={toggleTenderBookmark.isPending}
              onClick={() => {
                if (!id || toggleTenderBookmark.isPending) return;
                toggleTenderBookmark.mutate({
                  tenderId: id,
                });
              }}
            />
          </div>
        }
      />

      {/* Header card */}
      <Card className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="font-display text-xl font-bold text-slate-900 leading-snug">
              {latestVersion.project_name}
            </h1>

            {/* Agency */}
            <div className="flex items-center gap-2 mt-3">
              <Icon
                icon="solar:buildings-line-duotone"
                className="w-5 h-5 text-slate-400"
              />
              {tender.agency ? (
                <Link
                  to={`/agencies/${tender.agency}`}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {latestVersion.dept_name || "ไม่ทราบหน่วยงาน"}
                </Link>
              ) : (
                <span className="text-slate-600">
                  {latestVersion.dept_name || "ไม่ทราบหน่วยงาน"}
                </span>
              )}
            </div>

            {/* Tags row */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <ProjectStatusBadge
                status={latestVersion.project_status as any}
              />
              {latestVersion.procurement_method && (
                <ProcurementMethodBadge
                  method={latestVersion.procurement_method}
                />
              )}
              {latestVersion.province_code && (
                <Badge variant="secondary">
                  <Icon
                    icon="solar:map-point-line-duotone"
                    className="w-3.5 h-3.5"
                  />
                  {PROVINCE_NAMES[latestVersion.province_code] ||
                    latestVersion.province_code}
                </Badge>
              )}
            </div>
          </div>

          {/* Key metrics */}
          <div className="flex flex-col items-end gap-2">
            {latestVersion.sum_price_agree && (
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase tracking-wider">
                  งบประมาณ
                </p>
                <p className="font-display text-2xl font-bold text-slate-900 leading-tight">
                  {formatNumber(Math.round(latestVersion.sum_price_agree / 100))}
                </p>
                <p className="text-[10px] text-slate-500 leading-none text-right">บาท</p>
              </div>
            )}
          </div>
        </div>

        {/* Key dates */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
          <MetricItem
            label="วันประกาศ"
            value={
              latestVersion.published_at || latestVersion.effective_at
                ? formatDate(
                    latestVersion.published_at || latestVersion.effective_at
                  )
                : "-"
            }
            icon="solar:calendar-line-duotone"
          />
          <MetricItem
            label="ปีงบประมาณ"
            value={
              latestVersion.budget_year
                ? `พ.ศ. ${latestVersion.budget_year}`
                : "-"
            }
            icon="solar:calendar-date-line-duotone"
          />
          <MetricItem
            label="รหัสโครงการ"
            value={latestVersion.project_id || "-"}
            icon="solar:hashtag-line-duotone"
          />
          <MetricItem
            label="เวอร์ชัน"
            value={`v${latestVersion.version_number}`}
            icon="solar:refresh-circle-line-duotone"
          />
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <UnderlineTabsList className="mb-6">
          <UnderlineTabsTrigger value="overview">ภาพรวม</UnderlineTabsTrigger>
          <UnderlineTabsTrigger value="timeline">ไทม์ไลน์</UnderlineTabsTrigger>
          <UnderlineTabsTrigger value="documents">เอกสาร</UnderlineTabsTrigger>
          <UnderlineTabsTrigger value="companies">บริษัท</UnderlineTabsTrigger>
          <UnderlineTabsTrigger value="rfq">
            บทสรุปผู้บริหาร
          </UnderlineTabsTrigger>
        </UnderlineTabsList>

        <TabsContent value="overview">
          <TenderOverviewTab version={latestVersion} />
        </TabsContent>

        <TabsContent value="timeline">
          <TenderTimelineTab tenderId={id!} />
        </TabsContent>

        <TabsContent value="documents">
          <TenderDocumentsTab tenderId={id!} />
        </TabsContent>

        <TabsContent value="companies">
          <TenderCompaniesTab tenderId={id!} />
        </TabsContent>

        <TabsContent value="rfq">
          <TenderRfqTab tenderId={id!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MetricItem({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 bg-slate-100 rounded-lg">
        <Icon icon={icon} className="w-4 h-4 text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-medium text-slate-900">{value}</p>
      </div>
    </div>
  );
}
